import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
} from "@mui/material";
import { Close } from "@mui/icons-material";

const drawerWidth = 240;

const SideNav = ({ isOpen, onClose, navLinks, user }) => {
  const location = useLocation();

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "?";

  const drawerContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#111827",
        boxSizing: "border-box",
        overflowY: "auto",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {/* Logo header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              bgcolor: "#E50000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: "0.85rem",
                letterSpacing: "-0.03em",
              }}
            >
              ST
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.875rem",
                color: "#F9FAFB",
                lineHeight: 1.2,
              }}
            >
              Sabre CWA Tours
            </Typography>
            <Typography
              sx={{ fontSize: "0.65rem", color: "#6B7280", lineHeight: 1 }}
            >
              Platform
            </Typography>
          </Box>
        </Box>

        {/* Mobile close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            display: { xs: "flex", md: "none" },
            color: "#6B7280",
            "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#D1D5DB" },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Divider */}
      <Box sx={{ mx: 2.5, bgcolor: "#1F2937", mb: 2 }} />

      {/* Nav section label */}
      <Typography
        sx={{
          px: 3,
          mb: 1,
          display: "block",
          color: "#4B5563",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.09em",
          fontSize: "0.62rem",
        }}
      >
        Main Menu
      </Typography>

      {/* Nav links */}
      <Box sx={{ flex: 1, px: 1.5 }}>
        <List
          dense
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          {navLinks.map(({ to, icon, label }) => {
            const isActive = location.pathname === to;
            return (
              <ListItemButton
                key={to}
                component={NavLink}
                to={to}
                onClick={onClose}
                sx={{
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1.05,
                  color: isActive ? "#FFFFFF" : "#9CA3AF",
                  bgcolor: isActive ? "#E50000" : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: isActive ? "#C40000" : "rgba(255,255,255,0.06)",
                    color: isActive ? "#FFFFFF" : "#D1D5DB",
                  },
                  "&.active": { bgcolor: "#E50000", color: "#FFFFFF" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "inherit",
                  }}
                >
                  {React.cloneElement(icon, { sx: { fontSize: 18 } })}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: "0.84rem",
                    fontWeight: isActive ? 600 : 500,
                    lineHeight: 1.4,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* User info footer */}
      <Box
        sx={{
          mx: 1.5,
          mb: 2,
          px: 1.5,
          py: 1.25,
          borderRadius: 1.5,
          bgcolor: "#1F2937",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          border: "1px solid #374151",
        }}
      >
        <Avatar
          src={user?.avatarUrl}
          sx={{
            width: 30,
            height: 30,
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: "#E50000",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "#E5E7EB",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.67rem",
              color: "#6B7280",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.email ?? ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={isOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
            boxShadow: "4px 0 32px rgba(0,0,0,0.25)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
            borderRight: "1px solid #1F2937",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default SideNav;
